import { supabase } from './supabase';
import MatchingScoreService from '../services/matchingScoreService';
import { computeShortIdFromUUID } from './encryption';
import { formatError, logError } from './errorLogger';
import cacheService from '../services/cacheService.js';
import { escapePostgresLike } from './sqlSecurity';

// Simple validation functions
const isValidJobType = (type) => type && typeof type === 'string' && type.length > 0;
const isValidExperienceLevel = (level) => level && typeof level === 'string' && level.length > 0;

// Use Redis cache exclusively for better performance and multi-instance support
// In-memory cache removed in favor of Redis

const jobService = {
  // Get job by ID for public viewing
  getJobById: async (jobId) => {
    try {
      if (!jobId) {
        return { success: false, error: 'Job ID is required' };
      }
      
      // Try to get from cache first
      if (cacheService.isAvailable()) {
        const cachedJob = await cacheService.getJob(jobId);
        if (cachedJob) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[jobService] Job loaded from cache:', jobId);
          }
          return { success: true, data: cachedJob };
        }
      }
      
      // Use a simpler query approach
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          *,
          employer:user_profiles!jobs_employer_id_fkey(
            id,
            full_name,
            company_name,
            location,
            profile_picture,
            company_logo,
            website,
            company_size,
            founded_year
          )
        `)
        .eq('id', jobId)
        .in('status', ['published', 'active'])
        .single();
      
      if (error) {
        return formatError('jobService.getJobById', error, 'Failed to retrieve job details');
      }
      
      if (!data) {
        return { success: false, error: 'Job not found or has been removed' };
      }
      
      // Normalize field names for consistency
      const normalizedData = {
        ...data,
        // Ensure skills_required is always available
        skills_required: data.skills_required || data.required_skills || []
      };
      
      // Cache the job
      if (cacheService.isAvailable()) {
        await cacheService.setJob(jobId, normalizedData);
        if (process.env.NODE_ENV === 'development') {
          console.log('[jobService] Job cached:', jobId);
        }
      }
      
      return { success: true, data: normalizedData };
    } catch (error) {
      return formatError('jobService.getJobById', error, 'An unexpected error occurred');
    }
  },
  
  // Get all active jobs with pagination and filtering
  getJobs: async (options = {}) => {
    try {
      const { 
        limit = 20,
        offset = 0,
        search = '',
        jobType = '',
        experienceLevel = '',
        skills = [],
        sortBy = 'created_at',
        sortOrder = 'desc',
        minSalary,
        maxSalary,
        userId, // Add userId parameter to check for bookmarks
        employerId // Add employerId parameter to filter by employer
      } = options;

      // Create a cache key from search parameters (exclude userId for shared caching)
      const searchParams = {
        limit,
        offset,
        search: search?.toLowerCase() || '',
        jobType: jobType || '',
        experienceLevel: experienceLevel || '',
        skills: Array.isArray(skills) ? skills.sort().join(',') : '',
        sortBy: sortBy || 'created_at',
        sortOrder: sortOrder || 'desc',
        minSalary: minSalary || '',
        maxSalary: maxSalary || '',
        employerId: employerId || ''
      };
      
      // Create a simple hash from the search parameters
      const searchHash = JSON.stringify(searchParams);
      
      // Try to get from cache first (5min TTL)
      if (cacheService.isAvailable()) {
        const cachedResults = await cacheService.getJobSearch(searchHash);
        if (cachedResults) {
          if (import.meta.env?.MODE === 'development' || import.meta.env?.DEV) {
            console.log('[jobService] Job search loaded from cache');
          }
          
          // If userId provided, add bookmark and application information to cached results
          if (userId && cachedResults.data?.length > 0) {
            try {
              const { data: bookmarks } = await supabase
                .from('job_bookmarks')
                .select('job_id')
                .eq('user_id', userId);
              
              const { data: applications } = await supabase
                .from('job_applications')
                .select('job_id')
                .eq('applicant_id', userId);
              
              if (bookmarks || applications) {
                const bookmarkedJobIds = new Set(bookmarks?.map(b => b.job_id) || []);
                const appliedJobIds = new Set(applications?.map(a => a.job_id) || []);
                const jobsWithMetadata = cachedResults.data.map(job => ({
                  ...job,
                  isBookmarked: bookmarkedJobIds.has(job.id),
                  isApplied: appliedJobIds.has(job.id)
                }));
                return { ...cachedResults, data: jobsWithMetadata };
              }
            } catch (err) {
              logError('jobService.getJobs.cachedMetadata', err);
            }
          }
          
          return cachedResults;
        }
      }

      let query = supabase
        .from('jobs')
        .select(`
          id,
          public_slug,
          title,
          description,
          job_type,
          experience_level,
          location,
          is_remote,
          skills_required,
          budget_min,
          budget_max,
          currency,
          applications_count,
          drafted_by,
          published_by,
          drafted_at,
          published_at,
          created_at,
          employer:user_profiles!jobs_employer_id_fkey(
            id,
            full_name,
            company_name,
            location,
            profile_picture,
            company_logo
          )
        `)
        .in('status', ['published', 'active'])
        .neq('status', 'expired')
        .or(`application_deadline.is.null,application_deadline.gte.${new Date().toISOString()}`);

      // Apply filters
      if (search) {
        const escapedSearch = escapePostgresLike(search);
        query = query.or(`title.ilike.%${escapedSearch}%,description.ilike.%${escapedSearch}%,company_name.ilike.%${escapedSearch}%`);
      }

      if (jobType) {
        // Normalize underscore to hyphen format to match DB values (e.g. 'full_time' -> 'full-time')
        const normalizedJobType = jobType.replace(/_/g, '-');
        if (isValidJobType(normalizedJobType)) {
          query = query.eq('job_type', normalizedJobType);
        }
      }

      if (experienceLevel) {
        // Validate experience level before adding to query
        if (isValidExperienceLevel(experienceLevel)) {
          query = query.eq('experience_level', experienceLevel);
        }
      }

      // Filter by employer if specified
      if (employerId) {
        query = query.eq('employer_id', employerId);
      }
      
      // Apply remote filter if specified - removed as no longer needed
      // The remote filter has been removed from the UI
      
      // Apply salary filters — find jobs whose budget range overlaps with the filter range.
      // A job overlaps when: budget_min <= maxSalary AND budget_max >= minSalary
      if (minSalary) {
        query = query.gte('budget_max', minSalary);
      }
      
      if (maxSalary) {
        query = query.lte('budget_min', maxSalary);
      }

      if (skills?.length > 0) {
        // Filter for skills using JSONB contains operator 
        const validSkills = skills.filter(skill => skill && skill.trim()).map(skill => skill.trim());
        
        if (validSkills.length > 0) {
          try {
            // Use multiple individual contains checks with OR
            // This approach avoids complex JSONB syntax issues
            const skillQueries = [];
            
            const now = new Date().toISOString();
            for (const skill of validSkills) {
              // Use the Supabase contains method properly
              skillQueries.push(
                supabase
                  .from('jobs')
                  .select('id')
                  .in('status', ['published', 'active'])
                  .neq('status', 'expired')
                  .or(`application_deadline.is.null,application_deadline.gte.${now}`)
                  .contains('skills_required', JSON.stringify([skill]))
              );
            }
            
            // Execute queries and collect matching job IDs
            const matchingJobIds = new Set();
            
            for (const skillQuery of skillQueries) {
              const { data: matchingJobs, error } = await skillQuery;
              if (error) {
                continue; // Skip this skill and continue with others
              }
              
              if (matchingJobs) {
                matchingJobs.forEach(job => matchingJobIds.add(job.id));
              }
            }
            
            // Apply the collected job IDs to the main query
            if (matchingJobIds.size > 0) {
              query = query.in('id', Array.from(matchingJobIds));
            } else {
              // No matches found, return empty result
              query = query.eq('id', '00000000-0000-0000-0000-000000000000');
            }
          } catch {
            // In case of error, continue without skills filter
          }
        }
      }

      // Apply pagination
      query = query.range(offset, offset + limit - 1);

      // Apply sorting
      try {
        // For salary fields, use the correct column names from the database
        if (sortBy.includes('salary') || sortBy.includes('min_salary') || sortBy.includes('max_salary')) {
          const ascending = sortOrder === 'asc';
          
          // Use the actual field names from the database
          const fieldName = sortBy.includes('min') || sortBy === 'salary_range_min' ? 
            'budget_min' : // For minimum salary (budget_min in the database)
            'budget_max';  // For maximum salary (budget_max in the database)
          
          // Apply the sort with the correct field name
          query = query.order(fieldName, { ascending, nullsLast: true });
        } else if (sortBy === 'hourly_rate_min' || sortBy === 'hourly_rate_max') {
          const ascending = sortOrder === 'asc';
          // Use the hourly rate fields directly as they match the database columns
          query = query.order(sortBy, { ascending, nullsLast: true });
        } else {
          // For non-salary fields
          const validSortFields = ['created_at', 'title', 'location'];
          const sortField = validSortFields.includes(sortBy) ? sortBy : 'created_at';
          const ascending = sortOrder === 'asc';
          
          query = query.order(sortField, { ascending });
        }
      } catch {
        // Fall back to created_at if there's an error with the sorting
        query = query.order('created_at', { ascending: false });
      }

      try {
        const { data, error, count } = await query;

        if (error) {
          // If the error is related to sorting, try again with a default sort
          if (error.message && (error.message.includes('order') || error.message.includes('sort'))) {
            // Remove the problematic order and use a safe default
            query = query.order('created_at', { ascending: false });
            
            // Try again with the fallback sort
            const fallbackResult = await query;
            
            if (fallbackResult.error) {
              logError('jobService.getJobs.fallbackSort', fallbackResult.error, { options });
              return { success: false, error: 'Failed to sort results. ' + fallbackResult.error.message };
            }
            
            return { success: true, data: fallbackResult.data, total: fallbackResult.count };
          }
          
          // Log the error with detailed information
          logError('jobService.getJobs.query', error, { 
            options, 
            queryParams: JSON.stringify(options)
          });
          
          return { success: false, error: error.message };
        }
        
        // If userId is provided, fetch bookmark and application information
        if (userId && data && data.length > 0) {
          try {
            // Get all bookmarks for this user
            const { data: bookmarks, error: bookmarkError } = await supabase
              .from('job_bookmarks')
              .select('job_id')
              .eq('user_id', userId);
            
            // Get all applications for this user
            const { data: applications, error: applicationsError } = await supabase
              .from('job_applications')
              .select('job_id')
              .eq('applicant_id', userId);
            
            if ((!bookmarkError && bookmarks) || (!applicationsError && applications)) {
              // Create Sets for efficient lookup
              const bookmarkedJobIds = new Set(bookmarks?.map(b => b.job_id) || []);
              const appliedJobIds = new Set(applications?.map(a => a.job_id) || []);
              
              // Add isBookmarked and isApplied flags to each job
              const jobsWithMetadata = data.map(job => ({
                ...job,
                isBookmarked: bookmarkedJobIds.has(job.id),
                isApplied: appliedJobIds.has(job.id)
              }));
              
              return { success: true, data: jobsWithMetadata, total: count };
            }
          } catch (err) {
            logError("jobService.getJobs.metadata", err);
            // Continue without metadata if there's an error
          }
        }

        // Cache the results (5min TTL, shared across users)
        const resultToCache = { success: true, data, total: count };
        if (cacheService.isAvailable()) {
          await cacheService.setJobSearch(searchHash, resultToCache);
          if (import.meta.env?.MODE === 'development' || import.meta.env?.DEV) {
            console.log('[jobService] Job search results cached');
          }
        }

        return { success: true, data, total: count };
      } catch (error) {
        return formatError('jobService.getJobs', error, 'Failed to load jobs');
      }
    } catch (error) {
      return formatError('jobService.getJobs', error, 'Failed to load jobs');
    }
  },

  // Get job by ID with full details (supports both UUID and 8-character short ID)
  getJobWithFullDetails: async (jobId) => {
    try {
      // Check Redis cache first
      if (cacheService.isAvailable()) {
        const cacheKey = `job_full_v2:${jobId}`;
        const cached = await cacheService.get(cacheKey);
        if (cached) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[jobService] Full job details loaded from cache:', jobId);
          }
          return { success: true, data: cached };
        }
      }

      // Check if it's a UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const isUUID = uuidRegex.test(jobId);
      
      if (isUUID) {
        // Direct UUID lookup - optimized field selection
        const { data, error } = await supabase
          .from('jobs')
          .select(`
            id,
            public_slug,
            title,
            description,
            requirements,
            responsibilities,
            job_type,
            experience_level,
            location,
            is_remote,
            skills_required,
            budget_min,
            budget_max,
            currency,
            applications_count,
            status,
            application_deadline,
            drafted_by,
            published_by,
            drafted_at,
            published_at,
            created_at,
            updated_at,
            employer:user_profiles!jobs_employer_id_fkey(
              id,
              full_name,
              company_name,
              location,
              profile_picture,
              company_logo,
              bio,
              website
            ),
            applications:job_applications(
              id,
              status,
              applied_at
            )
          `)
          .eq('id', jobId)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            return { success: false, error: 'Job not found' };
          }
          return { success: false, error: error.message };
        }

        // Cache the result in Redis (10 min TTL)
        if (cacheService.isAvailable()) {
          const cacheKey = `job_full_v2:${jobId}`;
          await cacheService.set(cacheKey, data, 600);
          if (process.env.NODE_ENV === 'development') {
            console.log('[jobService] Full job details cached:', jobId);
          }
        }
        return { success: true, data };
      } else {
        // 8-character short ID lookup - optimized field selection
        const { data: jobs, error } = await supabase
          .from('jobs')
          .select(`
            id,
            public_slug,
            title,
            description,
            requirements,
            responsibilities,
            job_type,
            experience_level,
            location,
            is_remote,
            skills_required,
            budget_min,
            budget_max,
            currency,
            applications_count,
            status,
            application_deadline,
            drafted_by,
            published_by,
            drafted_at,
            published_at,
            created_at,
            updated_at,
            employer:user_profiles!jobs_employer_id_fkey(
              id,
              full_name,
              company_name,
              location,
              profile_picture,
              company_logo,
              bio,
              website
            ),
            applications:job_applications(
              id,
              status,
              applied_at
            )
          `)
          .in('status', ['published', 'active']);

        if (error) {
          return { success: false, error: error.message };
        }

        if (jobs && jobs.length > 0) {
          // Find job where ID matches the short ID (handles both 6 and 8 character IDs)
          const targetJob = jobs.find(job => {
            const shortId = computeShortIdFromUUID(job.id);
            // Match if the short ID starts with the provided ID (handles truncated slugs)
            return shortId === jobId || shortId.startsWith(jobId) || 
                   // Also check if the public_slug ends with this ID
                   (job.public_slug && job.public_slug.endsWith(jobId));
          });

          if (!targetJob) {
            return { success: false, error: 'Job not found' };
          }

          // Cache the result in Redis (10 min TTL)
          if (cacheService.isAvailable()) {
            const cacheKey = `job_full_v2:${jobId}`;
            await cacheService.set(cacheKey, targetJob, 600);
            if (process.env.NODE_ENV === 'development') {
              console.log('[jobService] Full job details cached (short ID):', jobId);
            }
          }
          return { success: true, data: targetJob };
        }
      }
    } catch {
      return { success: false, error: 'Failed to load job details' };
    }
  },

  // Create a new job
  createJob: async (jobData) => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .insert([jobData])
        .select()
        .single();

      if (error) {
        return formatError('jobService.createJob', error, 'Failed to create job');
      }
      
      // Invalidate job search caches when new job is created
      if (cacheService.isAvailable()) {
        await cacheService.invalidateJobSearches();
        if (process.env.NODE_ENV === 'development') {
          console.log('[jobService] Job search caches invalidated after job creation');
        }
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to create job' };
    }
  },

  // Update a job
  updateJob: async (jobId, updates) => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', jobId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      // Invalidate caches after job update
      if (cacheService.isAvailable()) {
        await Promise.all([
          cacheService.invalidateJob(jobId),
          cacheService.invalidateJobSearches(),
        ]);
        if (process.env.NODE_ENV === 'development') {
          console.log('[jobService] Job caches invalidated after update:', jobId);
        }
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to update job' };
    }
  },

  // Delete a job
  deleteJob: async (jobId) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', jobId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to delete job' };
    }
  },

  // Get jobs by employer
  getJobsByEmployer: async (employerId) => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('employer_id', employerId)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load employer jobs' };
    }
  },

  // Apply to a job
  applyToJob: async (applicationData) => {
    try {
      // Check if the job is still accepting applications before proceeding
      if (applicationData.job_id) {
        const { data: jobCheck, error: jobCheckError } = await supabase
          .from('jobs')
          .select('id, status, application_deadline')
          .eq('id', applicationData.job_id)
          .single();

        if (jobCheckError) {
          return { success: false, error: 'Unable to verify job status. Please try again.' };
        }

        if (jobCheck?.status === 'expired') {
          return { success: false, error: 'This job posting has expired and is no longer accepting applications.' };
        }

        if (jobCheck?.application_deadline && new Date(jobCheck.application_deadline) < new Date()) {
          return { success: false, error: 'The application deadline for this job has passed.' };
        }

        if (!['active', 'published'].includes(jobCheck?.status)) {
          return { success: false, error: 'This job is not currently accepting applications.' };
        }
      }

      // Get source tracking data if available
      let sourceData = {};
      try {
        const stored = sessionStorage.getItem('applicationSource');
        if (stored) {
          const parsedData = JSON.parse(stored);
          // Check if data is fresh (within 1 hour) and for correct job
          if (Date.now() - parsedData.timestamp < 60 * 60 * 1000 && 
              parsedData.jobId === applicationData.job_id) {
            sourceData = {
              application_source: parsedData.source || 'direct',
              referrer_url: parsedData.referrerUrl,
              utm_source: parsedData.utmSource,
              utm_medium: parsedData.utmMedium,
              utm_campaign: parsedData.utmCampaign
            };
          }
        }
      } catch (error) {
        console.warn('Could not parse source tracking data:', error);
      }

      // If no source data found, detect from current URL
      if (!sourceData.application_source) {
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const referrerUrl = document.referrer;
          
          // Simple source detection
          let detectedSource = 'direct';
          if (urlParams.get('utm_source')?.includes('linkedin')) detectedSource = 'linkedin';
          else if (urlParams.get('utm_source')?.includes('indeed')) detectedSource = 'job_boards';
          else if (urlParams.get('utm_source')?.includes('referral')) detectedSource = 'referral';
          else if (referrerUrl && !referrerUrl.includes(window.location.hostname)) detectedSource = 'other';
          
          sourceData = {
            application_source: detectedSource,
            referrer_url: referrerUrl || null,
            utm_source: urlParams.get('utm_source') || null,
            utm_medium: urlParams.get('utm_medium') || null,
            utm_campaign: urlParams.get('utm_campaign') || null
          };
        } catch (error) {
          sourceData = { application_source: 'direct' };
        }
      }

      // First, create the application with source tracking
      const { data, error } = await supabase
        .from('job_applications')
        .insert({
          job_id: applicationData.job_id,
          applicant_id: applicationData.applicant_id,
          cover_letter: applicationData.cover_letter,
          proposed_rate: applicationData.proposed_rate,
          proposed_rate_min: applicationData.proposed_rate_min,
          proposed_rate_max: applicationData.proposed_rate_max,
          availability_start: applicationData.availability_start,
          portfolio_links: applicationData.portfolio_links || [],
          status: 'pending',
          match_percentage: 0, // Will be updated after calculation
          ...sourceData // Include source tracking data
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          return { success: false, error: 'You have already applied to this job' };
        }
        return { success: false, error: error.message };
      }

      // If form responses exist, save them
      if (applicationData.formResponses && applicationData.formId) {
        const { error: formError } = await supabase
          .from('form_responses')
          .insert({
            form_id: applicationData.formId,
            application_id: data.id,
            va_id: applicationData.applicant_id,
            responses: applicationData.formResponses
          });

        if (formError) {
          console.error('Error saving form responses:', formError);
          // Don't fail the entire application if form responses fail to save
        }
      }

      // Clear stored source data after successful application
      try {
        sessionStorage.removeItem('applicationSource');
      } catch (error) {
        // Ignore sessionStorage errors
      }

      // Calculate and update match score (non-blocking)
      // This is done asynchronously to not block the application process
      MatchingScoreService.calculateAndUpdateApplicationScore(
        data.id,
        applicationData.job_id,
        applicationData.applicant_id
      ).catch(error => {
        // Log the error but don't fail the application process
        console.warn('Match score calculation failed (non-blocking):', error);
      });

      // ========================================
      // Send email notification to employer
      // ========================================
      try {
        // Get job and employer details for email
        const { data: jobData, error: jobError } = await supabase
          .from('jobs')
          .select(`
            title,
            employer_id,
            user_profiles!jobs_employer_id_fkey (
              full_name,
              email
            )
          `)
          .eq('id', applicationData.job_id)
          .single();

        if (!jobError && jobData) {
          const employerProfile = jobData.user_profiles;
          
          // Get applicant details
          const { data: applicantData, error: applicantError } = await supabase
            .from('user_profiles')
            .select('full_name, email')
            .eq('id', applicationData.applicant_id)
            .single();

          if (!applicantError && applicantData && employerProfile) {
            // Format application date
            const applicationDate = new Date().toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            });

            // Build application URL
            const applicationUrl = `https://dashboard.vasprouts.com/employer-dashboard?application_id=${data.id}`;

            // Get cover letter preview (first 150 chars)
            let coverLetterPreview = null;
            if (applicationData.cover_letter) {
              coverLetterPreview = applicationData.cover_letter.substring(0, 150);
              if (applicationData.cover_letter.length > 150) {
                coverLetterPreview += '...';
              }
            }

            // Send email via edge function (non-blocking)
            supabase.functions.invoke('send-email', {
              body: {
                emailType: 'new-application',
                email: employerProfile.email,
                fullName: employerProfile.full_name,
                jobTitle: jobData.title,
                applicantName: applicantData.full_name,
                applicationDate: applicationDate,
                coverLetterPreview: coverLetterPreview,
                applicationUrl: applicationUrl
              }
            }).catch(emailError => {
              // Log email error but don't fail application
              console.error('Failed to send application email notification:', emailError);
            });
          }
        }
      } catch (emailError) {
        // Log error but don't block application success
        console.error('Error sending employer notification email:', emailError);
      }

      return { success: true, data };
    } catch {
      return { success: false, error: 'Failed to submit application' };
    }
  },

  // Get applications for a job
  getJobApplications: async (jobId) => {
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .select(`
          *,
          applicant:user_profiles!job_applications_applicant_id_fkey(
            id,
            full_name,
            location,
            hourly_rate,
            skills,
            experience_years,
            profile_picture,
            bio
          )
        `)
        .eq('job_id', jobId)
        .order('applied_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load applications' };
    }
  },

  // Get user's applications
  getUserApplications: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .select(`
          id,
          job_id,
          applicant_id,
          cover_letter,
          proposed_rate,
          proposed_rate_min,
          proposed_rate_max,
          status,
          employer_notes,
          applied_at,
          updated_at,
          interview_scheduled_at,
          match_percentage,
          availability_start,
          portfolio_links,
          is_archived,
          archived_at,
          job:jobs(
            id,
            public_slug,
            title,
            company_name,
            job_type,
            location,
            budget_min,
            budget_max,
            hourly_rate_min,
            hourly_rate_max,
            status,
            is_remote,
            experience_level,
            employer:user_profiles!jobs_employer_id_fkey(
              id,
              full_name,
              company_name,
              profile_picture,
              company_logo
            )
          ),
          interview_data:interview_schedules(
            id,
            scheduled_at,
            feedback,
            hiring_notes,
            hiring_status,
            offer_amount,
            decision_date,
            rejection_reason
          )
        `)
        .eq('applicant_id', userId)
        .order('applied_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      // Process the data to flatten interview_data if it exists
      const processedData = data?.map(application => {
        if (application.interview_data && application.interview_data.length > 0) {
          // Take the most recent interview data
          const latestInterview = application.interview_data[0];
          return {
            ...application,
            interview_data: latestInterview
          };
        }
        return application;
      });

      return { success: true, data: processedData };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load user applications' };
    }
  },

  // Archive an application (VA-side organizational action)
  archiveApplication: async (applicationId, userId) => {
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .update({
          is_archived: true,
          archived_at: new Date().toISOString()
        })
        .eq('id', applicationId)
        .eq('applicant_id', userId)
        .select('id, is_archived, archived_at')
        .single();

      if (error) {
        return formatError('jobService.archiveApplication', error, 'Failed to archive application');
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to archive application' };
    }
  },

  // Unarchive an application
  unarchiveApplication: async (applicationId, userId) => {
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .update({
          is_archived: false,
          archived_at: null
        })
        .eq('id', applicationId)
        .eq('applicant_id', userId)
        .select('id, is_archived, archived_at')
        .single();

      if (error) {
        return formatError('jobService.unarchiveApplication', error, 'Failed to unarchive application');
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to unarchive application' };
    }
  },

  // Update application status
  updateApplicationStatus: async (applicationId, status, notes = '') => {
    try {
      const updates = {
        status,
        updated_at: new Date().toISOString()
      };

      if (notes) {
        updates.notes = notes;
      }

      const { data, error } = await supabase
        .from('job_applications')
        .update(updates)
        .eq('id', applicationId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to update application' };
    }
  },

  // Recalculate match scores for all applications of a job
  recalculateJobMatchScores: async (jobId) => {
    try {
      const result = await MatchingScoreService.updateJobApplicationScores(jobId);
      
      if (result.success) {
        return { 
          success: true, 
          message: `Successfully updated ${result.data.successful} application match scores`,
          data: result.data
        };
      } else {
        return { success: false, error: result.error };
      }
    } catch {
      return { success: false, error: 'Failed to recalculate match scores' };
    }
  },

  // Calculate match score for a specific candidate and job (without applying)
  calculateCandidateJobMatch: async (candidateId, jobId) => {
    try {
      // Get job details
      const { data: job, error: jobError } = await supabase
        .from('jobs')
        .select(`
          id,
          title,
          skills_required,
          min_experience_years,
          budget_min,
          budget_max,
          hourly_rate_min,
          hourly_rate_max,
          location,
          is_remote
        `)
        .eq('id', jobId)
        .single();

      if (jobError) throw jobError;

      // Get candidate profile
      const { data: candidate, error: candidateError } = await supabase
        .from('user_profiles')
        .select(`
          id,
          skills,
          experience_years,
          hourly_rate,
          location,
          user_analytics (
            average_rating,
            total_earnings
          )
        `)
        .eq('id', candidateId)
        .single();

      if (candidateError) throw candidateError;

      // Calculate match score
      const matchResult = MatchingScoreService.calculateMatchScore(candidate, job);

      return {
        success: true,
        data: {
          candidateId,
          jobId,
          matchScore: matchResult.overallScore,
          breakdown: matchResult.breakdown,
          factors: matchResult.matchFactors,
          weights: matchResult.weights
        }
      };

    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },
  getUserApplicationsWithJobDetails: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .select(`
          *,
          job:jobs(
            id,
            title,
            company_name,
            location,
            is_remote,
            budget_min,
            budget_max,
            status,
            drafted_by,
            published_by,
            drafted_at,
            published_at
          )
        `)
        .eq('applicant_id', userId)
        .order('applied_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load applications' };
    }
  },

  // Toggle job bookmark
  toggleBookmark: async (userId, jobId) => {
    try {
      // Check if bookmark exists - don't use single() which expects exactly one result
      const { data: existingBookmarks, error: fetchError } = await supabase
        .from('job_bookmarks')
        .select('id')
        .eq('user_id', userId)
        .eq('job_id', jobId);

      if (fetchError) {
        return { success: false, error: fetchError.message };
      }

      // Check if we found any existing bookmarks
      if (existingBookmarks && existingBookmarks.length > 0) {
        // Remove bookmark
        const { error } = await supabase
          .from('job_bookmarks')
          .delete()
          .eq('user_id', userId)
          .eq('job_id', jobId);

        if (error) {
          return { success: false, error: error.message };
        }

        return { success: true, bookmarked: false };
      } else {
        // Add bookmark
        const { error } = await supabase
          .from('job_bookmarks')
          .insert({
            user_id: userId,
            job_id: jobId
          });

        if (error) {
          return { success: false, error: error.message };
        }

        return { success: true, bookmarked: true };
      }
    } catch {
      return { success: false, error: 'Failed to toggle bookmark' };
    }
  },

  // Get user's bookmarked jobs
  getUserBookmarks: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('job_bookmarks')
        .select(`
          *,
          job:jobs(
            *,
            employer:user_profiles!jobs_employer_id_fkey(
              id,
              full_name,
              company_name,
              profile_picture,
              company_logo
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch {
      return { success: false, error: 'Failed to load bookmarks' };
    }
  },

  // Get similar jobs based on skills and job type
  getSimilarJobs: async (jobId, limit = 5) => {
    try {
      // First get the reference job
      const { data: referenceJob } = await supabase
        .from('jobs')
        .select('skills_required, job_type, experience_level')
        .eq('id', jobId)
        .single();

      if (!referenceJob) {
        return { success: true, data: [] };
      }

      const { data, error } = await supabase
        .from('jobs')
        .select(`
          *,
          employer:user_profiles!jobs_employer_id_fkey(
            id,
            full_name,
            company_name,
            profile_picture,
            company_logo
          )
        `)
        .eq('status', 'published')
        .neq('id', jobId)
        .or(`job_type.eq.${referenceJob.job_type},experience_level.eq.${referenceJob.experience_level}`)
        .limit(limit);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch {
      return { success: false, error: 'Failed to load similar jobs' };
    }
  },

  // Check if user has already applied to a specific job
  checkUserApplicationStatus: async (userId, jobId) => {
    try {
      if (!userId || !jobId) {
        return { success: false, error: 'User ID and Job ID are required' };
      }

      // Verify authentication before making the query
      const { data: { user: currentUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !currentUser || currentUser.id !== userId) {
        return { 
          success: true, 
          hasApplied: false, 
          application: null 
        };
      }

      // Use a more RLS-friendly query approach by not using .single() which can cause 406 errors
      const { data, error } = await supabase
        .from('job_applications')
        .select('id, status, applied_at')
        .eq('applicant_id', userId)
        .eq('job_id', jobId)
        .limit(1);

      if (error) {
        // Log the error for debugging but don't fail the operation
        console.warn('Application status check error:', error);
        return { 
          success: true, 
          hasApplied: false, 
          application: null 
        };
      }

      // Check if we found any applications
      const hasApplied = data && data.length > 0;

      return { 
        success: true, 
        hasApplied, 
        application: hasApplied ? data[0] : null 
      };
    } catch (error) {
      console.warn('Application status check exception:', error);
      return { 
        success: true, 
        hasApplied: false, 
        application: null 
      };
    }
  },

  // Auto-match and shortlist candidates based on match percentage threshold
  autoMatchApplications: async (jobId, threshold = 75) => {
    try {
      if (!jobId) {
        return { success: false, error: 'Job ID is required' };
      }

      // Fetch all pending applications for the job with match scores
      const { data: applications, error: fetchError } = await supabase
        .from('job_applications')
        .select(`
          id,
          applicant_id,
          status,
          match_percentage,
          match_breakdown,
          applied_at
        `)
        .eq('job_id', jobId)
        .eq('status', 'pending')
        .gte('match_percentage', threshold)
        .order('match_percentage', { ascending: false });

      if (fetchError) {
        return { success: false, error: fetchError.message };
      }

      if (!applications || applications.length === 0) {
        return {
          success: true,
          message: 'No applications found matching the criteria',
          data: { 
            matched: 0, 
            shortlisted: 0,
            applications: []
          }
        };
      }

      // Update all matching applications to 'shortlisted' status
      const updatePromises = applications.map(app =>
        supabase
          .from('job_applications')
          .update({
            status: 'shortlisted',
            updated_at: new Date().toISOString()
          })
          .eq('id', app.id)
      );

      const results = await Promise.all(updatePromises);

      // Check for errors in updates
      const errors = results.filter(r => r.error);
      const shortlistedCount = results.filter(r => !r.error).length;

      if (errors.length > 0) {
        console.warn(`Auto-match: ${errors.length} applications failed to update`);
      }

      return {
        success: true,
        message: `Successfully auto-matched and shortlisted ${shortlistedCount} candidate(s)`,
        data: {
          matched: applications.length,
          shortlisted: shortlistedCount,
          applications: applications.map(app => ({
            id: app.id,
            matchPercentage: app.match_percentage,
            appliedAt: app.applied_at
          }))
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to auto-match applications'
      };
    }
  },

  // Get min/max salary bounds from active/published jobs for dynamic filter sliders
  getSalaryBounds: async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('budget_min, budget_max, job_type')
        .in('status', ['published', 'active'])
        .not('budget_max', 'is', null)
        .gt('budget_max', 0);

      if (error) {
        return formatError('jobService.getSalaryBounds', error, 'Failed to load salary bounds');
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return formatError('jobService.getSalaryBounds', error, 'Failed to load salary bounds');
    }
  }
};

export default jobService;